import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Upload, MoreVertical, Trash2, UserPlus, GripVertical, ChevronDown, Edit, User, Settings, LogOut, Crown, Edit2, Shield, Loader } from 'lucide-react';
import { getCurrentUserData } from '../utils/auth';
import { getUserInitials, getFamilyInitials } from '../utils/userUtils';
import { checkAuthentication, handleAuthFailure } from '../utils/authCheck';
import { checkUserExists, getMyFamilyPermissions } from '../services/familiesApi';
import ImageUploadWithPreview from './ImageUploadWithPreview';
import Premium from './Premium';

const Famlis = () => {
    const navigate = useNavigate();
    const [famlis, setFamlis] = useState([]);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showMemberModal, setShowMemberModal] = useState(false);
    const [editingMemberIndex, setEditingMemberIndex] = useState(null);
    const [activeMemberMenu, setActiveMemberMenu] = useState(null);
    const [activeMenu, setActiveMenu] = useState(null);
    const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [editingFamily, setEditingFamily] = useState(null);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const dropdownRef = useRef(null);
    const membersRef = useRef(null);
    const userMenuRef = useRef(null);
    const memberMenuRef = useRef(null);
    const familyMenuRefs = useRef({});

    const [formData, setFormData] = useState({
        photo: null,
        photoPreview: null,
        name: '',
        tagline: '',
        description: '',
        members: []
    });

    const [memberFormData, setMemberFormData] = useState({
        name: '',
        email: '',
        photo: null,
        photoPreview: null,
        isAdmin: false
    });

    const [checkingUser, setCheckingUser] = useState(false);
    const [userCheckResult, setUserCheckResult] = useState(null);
    const [userPermissions, setUserPermissions] = useState(null); // Stores current user's permissions for the family being viewed/edited

    // Debug: Track showMemberModal state changes
    useEffect(() => {
        console.log('[Famlis] showMemberModal state changed to:', showMemberModal);
    }, [showMemberModal]);

    // Debug: Track formData.members changes
    useEffect(() => {
        console.log('[Famlis] formData.members changed. New count:', formData.members.length);
        console.log('[Famlis] Members:', formData.members);
    }, [formData.members]);

    // Load families function (extracted so it can be called to refresh)
    const loadFamilies = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
            const response = await fetch(`${apiUrl}/v1/families`, {
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to load families');
            }

            const families = await response.json();
            setFamlis(families);

            // Load selected family from localStorage (just the ID)
            const selectedFamilyId = localStorage.getItem('selectedFamilyId');
            if (selectedFamilyId && families.length > 0) {
                const family = families.find(f => f.id === selectedFamilyId);
                setSelectedFamily(family);
            }
            
            console.log('[Famlis] Families loaded/refreshed:', families.length);
        } catch (error) {
            console.error('Error loading families:', error);
        }
    };

    // Load current user and families on mount
    useEffect(() => {
        // Load current user information from localStorage first (for quick display)
        const userData = getCurrentUserData();
        if (userData) {
            setCurrentUser(userData);
        }
        
        // Then refresh from API to ensure we have the latest data (including picture)
        const refreshUserData = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                const response = await fetch(`${apiUrl}/v1/auth/me`, {
                    credentials: 'include'
                });
                
                if (response.ok) {
                    const freshUserData = await response.json();
                    localStorage.setItem('currentUser', JSON.stringify(freshUserData));
                    setCurrentUser(freshUserData);
                }
            } catch (error) {
                console.error('Error refreshing user data:', error);
            }
        };
        
        refreshUserData();
        loadFamilies();
    }, []);
    
    // Auto-refresh families when window gains focus (e.g., after user logs in on another tab)
    useEffect(() => {
        const handleFocus = () => {
            console.log('[Famlis] Window focused - refreshing families to check for status updates');
            loadFamilies();
        };
        
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, []);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFamilyDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
            if (memberMenuRef.current && !memberMenuRef.current.contains(event.target)) {
                setActiveMemberMenu(null);
            }
            // Check if click is outside any family menu
            if (activeMenu !== null) {
                const clickedInsideMenu = Object.values(familyMenuRefs.current).some(
                    ref => ref && ref.contains(event.target)
                );
                if (!clickedInsideMenu) {
                    setActiveMenu(null);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeMenu]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (showCreateForm || showMemberModal || showPremiumModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showCreateForm, showMemberModal, showPremiumModal]);

    const handleFamilyPhotoSelect = (croppedImageData) => {
        setFormData({
            ...formData,
            photo: null,
            photoPreview: croppedImageData
        });
    };

    const handleMemberPhotoSelect = (croppedImageData) => {
        setMemberFormData({
            ...memberFormData,
            photo: null,
            photoPreview: croppedImageData
        });
    };

    const handleCheckUserEmail = async (email) => {
        if (!email || !email.includes('@')) {
            setUserCheckResult(null);
            return;
        }

        try {
            setCheckingUser(true);
            console.log('[Famlis] Checking if user exists:', email);
            const result = await checkUserExists(email);
            console.log('[Famlis] User check result:', result);
            setUserCheckResult(result);
            
            // If user exists, auto-fill their name and photo
            if (result.exists && result.name) {
                setMemberFormData(prev => ({
                    ...prev,
                    name: prev.name || result.name,  // Only update if name is empty
                    photoPreview: prev.photoPreview || result.photo
                }));
            }
        } catch (error) {
            console.error('[Famlis] Error checking user:', error);
            setUserCheckResult({ exists: false, status: 'pending_invitation' });
        } finally {
            setCheckingUser(false);
        }
    };

    const openAddMemberModal = () => {
        console.log('=== [Famlis] Opening Add Member Modal ===');
        console.log('[Famlis] Current showMemberModal state:', showMemberModal);
        setEditingMemberIndex(null);
        setMemberFormData({
            name: '',
            email: '',
            photo: null,
            photoPreview: null,
            isAdmin: false
        });
        setUserCheckResult(null);
        console.log('[Famlis] About to set showMemberModal to true...');
        setShowMemberModal(true);
        console.log('[Famlis] setShowMemberModal(true) called');
        // Use setTimeout to verify state was updated
        setTimeout(() => {
            console.log('[Famlis] After state update - showMemberModal should be:', true);
        }, 100);
    };

    const openEditMemberModal = (index) => {
        setEditingMemberIndex(index);
        const member = formData.members[index];
        setMemberFormData({
            name: member.name,
            email: member.email,
            photo: member.photo,
            photoPreview: member.photoPreview,
            isAdmin: member.isAdmin || false
        });
        // Set user check result based on existing member status
        setUserCheckResult({
            exists: member.status === 'active',
            status: member.status || 'pending_invitation',
            name: member.name,
            email: member.email,
            photo: member.photoPreview
        });
        setShowMemberModal(true);
        setActiveMemberMenu(null);
    };

    const handleSaveMember = () => {
        console.log('=== [Famlis] handleSaveMember called ===');
        console.log('[Famlis] Member form data:', memberFormData);
        console.log('[Famlis] Current formData.members:', formData.members);

        if (!memberFormData.name.trim() || !memberFormData.email.trim()) {
            console.error('[Famlis] Validation failed - name or email empty');
            alert('Name and Email are required');
            return;
        }

        if (editingMemberIndex !== null) {
            // Update existing member
            console.log('[Famlis] UPDATE MODE - index:', editingMemberIndex);
            const newMembers = [...formData.members];
            const existingMember = formData.members[editingMemberIndex];
            newMembers[editingMemberIndex] = {
                name: memberFormData.name,
                email: memberFormData.email,
                photo: memberFormData.photo,
                photoPreview: memberFormData.photoPreview,
                isAdmin: memberFormData.isAdmin,
                status: existingMember.status || 'active'  // Preserve existing status
            };
            console.log('[Famlis] Updated members array:', newMembers);
            setFormData({
                ...formData,
                members: newMembers
            });
        } else {
            // Add new member
            console.log('[Famlis] ADD MODE - creating new member');
            // Use the status from user check result if available, otherwise default to pending
            const memberStatus = userCheckResult?.status || 'pending_invitation';
            const newMember = {
                name: memberFormData.name,
                email: memberFormData.email,
                photo: memberFormData.photo,
                photoPreview: memberFormData.photoPreview || userCheckResult?.photo,
                isAdmin: memberFormData.isAdmin,
                status: memberStatus
            };
            console.log('[Famlis] New member object:', newMember);
            console.log('[Famlis] Using status from user check:', memberStatus);
            const updatedMembers = [...formData.members, newMember];
            console.log('[Famlis] Updated members array (with new member):', updatedMembers);
            setFormData({
                ...formData,
                members: updatedMembers
            });
        }

        console.log('[Famlis] Closing member modal...');
        setShowMemberModal(false);
        console.log('[Famlis] Member save complete!');
    };

    const removeMember = (index) => {
        const newMembers = formData.members.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            members: newMembers
        });
        setActiveMemberMenu(null);
    };

    const toggleAdmin = (index) => {
        const newMembers = [...formData.members];
        newMembers[index] = {
            ...newMembers[index],
            isAdmin: !newMembers[index].isAdmin
        };
        setFormData({
            ...formData,
            members: newMembers
        });
        setActiveMemberMenu(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // First, check if user is authenticated
            const isAuth = await checkAuthentication();
            if (!isAuth) {
                alert('Your session has expired. Please log in again.');
                handleAuthFailure();
                return;
            }

            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

            // Map members with isAdmin flag
            const validMembers = formData.members.map(m => ({
                name: m.name,
                email: m.email,
                photo: m.photoPreview || null,
                is_admin: m.isAdmin || false  // Convert to snake_case for backend
            }));

            // Build family data
            // For photo: if editing and photoPreview is null but original had photo, send empty string to delete
            // Otherwise send photoPreview (new photo) or null (no change if creating new)
            let photoValue = null;
            if (editingFamily) {
                // Editing mode: need to handle photo deletion explicitly
                if (formData.photoPreview) {
                    // New photo uploaded
                    photoValue = formData.photoPreview;
                } else if (editingFamily.photo && !formData.photoPreview) {
                    // Original had photo but now it's deleted (photoPreview is null)
                    photoValue = '';  // Empty string signals deletion
                } else {
                    // No photo before, no photo now - send null (no change)
                    photoValue = null;
                }
            } else {
                // Creating new family - just send photoPreview or null
                photoValue = formData.photoPreview || null;
            }
            
            const familyData = {
                name: formData.name,
                tagline: formData.tagline || null,
                description: formData.description || null,
                photo: photoValue,
                members: validMembers
            };

            if (editingFamily) {
                // Update existing family
                console.log('[Famlis] Updating family:', editingFamily.id);
                console.log('[Famlis] Family data being sent:', familyData);
                
                const response = await fetch(`${apiUrl}/v1/families/${editingFamily.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify(familyData)
                });

                console.log('[Famlis] Update response status:', response.status);
                console.log('[Famlis] Update response ok:', response.ok);

                if (response.status === 401) {
                    alert('Your session has expired. Please log in again.');
                    handleAuthFailure();
                    return;
                }

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('[Famlis] Update error response:', errorText);
                    let error;
                    try {
                        error = JSON.parse(errorText);
                    } catch {
                        error = { detail: errorText || 'Failed to update family' };
                    }
                    throw new Error(error.detail || 'Failed to update family');
                }

                const updatedFamily = await response.json();
                console.log('[Famlis] Updated family received:', updatedFamily);

                // Update the family in the famlis array immediately
                setFamlis(prevFamilies => 
                    prevFamilies.map(family => 
                        family.id === editingFamily.id ? updatedFamily : family
                    )
                );

                // Reload all families to get fresh member status (in case invitations were accepted)
                await loadFamilies();
                
                console.log('[Famlis] Family updated - reloaded families to get latest member statuses');

                // Update selected family if it was being edited
                if (selectedFamily?.id === editingFamily.id) {
                    setSelectedFamily(updatedFamily);
                }
            } else {
                // Create new family
                const response = await fetch(`${apiUrl}/v1/families`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify(familyData)
                });

                if (response.status === 401) {
                    alert('Your session has expired. Please log in again.');
                    handleAuthFailure();
                    return;
                }

                if (!response.ok) {
                    const error = await response.json().catch(() => ({}));
                    throw new Error(error.detail || 'Failed to create family');
                }

                const newFamily = await response.json();
                
                // Reload all families to get fresh data
                await loadFamilies();

                // Auto-select the new family
                setSelectedFamily(newFamily);
                localStorage.setItem('selectedFamilyId', newFamily.id);
            }

            // Reset form with current user as default admin
            const defaultMembers = currentUser ? [{
                name: currentUser.name,
                email: currentUser.email,
                photo: null,
                photoPreview: currentUser.picture,
                isAdmin: true,
                status: 'active'  // Current user (owner) is always active
            }] : [];

            setFormData({
                photo: null,
                photoPreview: null,
                name: '',
                tagline: '',
                description: '',
                members: defaultMembers
            });
            setShowCreateForm(false);
            setEditingFamily(null);
            setUserPermissions(null);
        } catch (error) {
            console.error('Error saving family:', error);
            alert(`Failed to save family: ${error.message}`);
        }
    };

    const deleteFamli = async (id) => {
        if (!window.confirm('Are you sure you want to delete this family? This action cannot be undone.')) {
            return;
        }

        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
            const response = await fetch(`${apiUrl}/v1/families/${id}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                throw new Error(error.detail || 'Failed to delete family');
            }

            const updatedFamlis = famlis.filter(f => f.id !== id);
            setFamlis(updatedFamlis);
            setActiveMenu(null);

            // Clear selected family if it was deleted
            if (selectedFamily?.id === id) {
                setSelectedFamily(null);
                localStorage.removeItem('selectedFamilyId');
            }
        } catch (error) {
            console.error('Error deleting family:', error);
            alert(`Failed to delete family: ${error.message}`);
        }
    };

    const toggleMenu = (id) => {
        setActiveMenu(activeMenu === id ? null : id);
    };

    const handleEdit = async (family, scrollToMembers = false) => {
        setEditingFamily(family);
        console.log('[Famlis] Editing family members:', family.members);
        
        // Fetch user's permissions for this family
        try {
            const permissions = await getMyFamilyPermissions(family.id);
            setUserPermissions(permissions);
            console.log('[Famlis] User permissions for family:', permissions);
            
            // If user doesn't have edit access, show view-only mode
            if (!permissions.can_edit) {
                console.log('[Famlis] User does not have edit permissions - showing view-only mode');
            }
        } catch (error) {
            console.error('[Famlis] Error fetching user permissions:', error);
            // If there's an error, assume no edit access for safety
            setUserPermissions({ can_edit: false, can_delete: false, is_admin: false, is_owner: false });
        }
        
        const members = family.members && family.members.length > 0
            ? family.members.map(m => {
                // Backend returns is_admin (snake_case), convert to isAdmin (camelCase)
                const isAdminValue = m.is_admin !== undefined ? m.is_admin : (m.isAdmin || false);
                console.log('[Famlis] Member:', m.name, 'is_admin:', m.is_admin, 'isAdmin:', m.isAdmin, 'final:', isAdminValue);
                return {
                    name: m.name || '',
                    email: m.email || '',
                    photo: null,
                    photoPreview: m.photo || null,
                    isAdmin: isAdminValue,
                    status: m.status || 'pending_invitation'  // Use status from backend, default to pending if not provided
                };
            })
            : (currentUser ? [{
                name: currentUser.name,
                email: currentUser.email,
                photo: null,
                photoPreview: currentUser.picture,
                isAdmin: true,
                status: 'active'  // Current user is always active
            }] : []);

        setFormData({
            photo: null,
            photoPreview: family.photo,
            name: family.name,
            tagline: family.tagline || '',
            description: family.description || '',
            members: members
        });
        setShowCreateForm(true);
        setActiveMenu(null);

        // Scroll to members section after modal opens
        if (scrollToMembers) {
            setTimeout(() => {
                if (membersRef.current) {
                    membersRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                    // Add a highlight effect
                    membersRef.current.classList.add('ring-2', 'ring-primary-blue', 'ring-opacity-50');
                    setTimeout(() => {
                        membersRef.current?.classList.remove('ring-2', 'ring-primary-blue', 'ring-opacity-50');
                    }, 2000);
                }
            }, 300);
        }
    };

    const goToDashboard = () => {
        if (famlis.length > 0) {
            navigate('/dashboard');
        } else {
            alert('Please create at least one family to continue!');
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        navigate('/login');
    };

    return (
        <div className="min-h-screen bg-secondary-gray">
            {/* Header */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center space-x-3">
                                <img src="/logo.png" alt="mifamli logo" className="h-10 w-10" />
                                <span className="text-xl font-bold text-primary-blue">mifamli</span>
                            </div>

                            {/* Mi Famlis Dropdown */}
                            {famlis.length > 0 && (
                                <div className="relative" ref={dropdownRef}>
                                    <button
                                        onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all"
                                        style={{
                                            background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)'
                                        }}
                                    >
                                        {selectedFamily ? (
                                            <>
                                                {selectedFamily.photo ? (
                                                    <img
                                                        src={selectedFamily.photo}
                                                        alt={selectedFamily.name}
                                                        className="w-6 h-6 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
                                                        {getFamilyInitials(selectedFamily.name)}
                                                    </div>
                                                )}
                                                <span>{selectedFamily.name}</span>
                                            </>
                                        ) : (
                                            <span>Mi Famlis</span>
                                        )}
                                        <ChevronDown className={`w-4 h-4 transition-transform ${showFamilyDropdown ? 'rotate-180' : ''}`} />
                                    </button>

                                    {/* Dropdown Menu */}
                                    {showFamilyDropdown && (
                                        <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                            {famlis.map((family) => (
                                                <button
                                                    key={family.id}
                                                    onClick={() => {
                                                        setSelectedFamily(family);
                                                        localStorage.setItem('selectedFamilyId', family.id);
                                                        setShowFamilyDropdown(false);
                                                        navigate('/dashboard');
                                                    }}
                                                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 ${selectedFamily?.id === family.id ? 'bg-blue-50' : ''
                                                        }`}
                                                >
                                                    {family.photo ? (
                                                        <img
                                                            src={family.photo}
                                                            alt={family.name}
                                                            className="w-10 h-10 rounded-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-pink-500 flex items-center justify-center text-white font-bold shadow-md">
                                                            {getFamilyInitials(family.name)}
                                                        </div>
                                                    )}
                                                    <div className="flex-1">
                                                        <p className="font-semibold text-gray-800">{family.name}</p>
                                                        {family.tagline && (
                                                            <p className="text-xs text-gray-500 italic truncate">"{family.tagline}"</p>
                                                        )}
                                                    </div>
                                                </button>
                                            ))}
                                            <div className="border-t border-gray-200 my-2"></div>
                                            <button
                                                onClick={() => {
                                                    setShowFamilyDropdown(false);
                                                    // Initialize with current user as admin
                                                    const defaultMembers = currentUser ? [{
                                                        name: currentUser.name,
                                                        email: currentUser.email,
                                                        photo: null,
                                                        photoPreview: currentUser.picture,
                                                        isAdmin: true,
                                                        status: 'active'  // Current user (owner) is always active
                                                    }] : [];

                                                    setFormData({
                                                        photo: null,
                                                        photoPreview: null,
                                                        name: '',
                                                        tagline: '',
                                                        description: '',
                                                        members: defaultMembers
                                                    });
                                                    setEditingFamily(null);
                                                    setShowCreateForm(true);
                                                }}
                                                className="w-full px-4 py-3 text-left hover:bg-primary-blue/5 flex items-center gap-3 text-primary-blue font-semibold"
                                            >
                                                <Plus className="w-5 h-5" />
                                                <span>Add Family</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-4">
                            {/* User Avatar Menu */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                                >
                                    {currentUser?.picture ? (
                                        <img
                                            src={currentUser.picture}
                                            alt={currentUser.name || 'User'}
                                            className="w-10 h-10 rounded-full object-cover shadow-md hover:shadow-lg transition-shadow border-2 border-white"
                                        />
                                    ) : (
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg shadow-md hover:shadow-lg transition-shadow border-2 border-white">
                                            {getUserInitials(currentUser?.name)}
                                        </div>
                                    )}
                                </button>

                                {/* User Dropdown Menu */}
                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                        <div className="px-4 py-3 border-b border-gray-200">
                                            <p className="text-sm font-semibold text-gray-800">{currentUser?.name || 'User Account'}</p>
                                            <p className="text-xs text-gray-500">{currentUser?.email || 'Manage your profile'}</p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                alert('Profile settings coming soon!');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <User className="w-5 h-5 text-gray-600" />
                                            <span>Profile</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                alert('Settings coming soon!');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <Settings className="w-5 h-5 text-gray-600" />
                                            <span>Settings</span>
                                        </button>
                                        <div className="border-t border-gray-200 my-2"></div>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                handleLogout();
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-red-600 font-semibold"
                                        >
                                            <LogOut className="w-5 h-5" />
                                            <span>Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Page Header */}
                {!showCreateForm && (
                    <div className="flex items-center justify-between mb-12">
                        <div>
                            <h1 className="text-4xl font-bold text-primary-blue mb-2">
                                {famlis.length === 0 ? 'Create Your First Family Hub' : 'Your Families'}
                            </h1>
                            <p className="text-lg text-gray-600">
                                {famlis.length === 0
                                    ? 'Set up your family to start organizing together'
                                    : 'Manage your family groups and members'}
                            </p>
                        </div>

                        <div className="relative">
                            <button
                                onClick={() => {
                                    // Check if user has reached free tier limit (1 family)
                                    if (famlis.length >= 1) {
                                        setShowPremiumModal(true);
                                        return;
                                    }

                                    // Initialize with current user as admin
                                    const defaultMembers = currentUser ? [{
                                        name: currentUser.name,
                                        email: currentUser.email,
                                        photo: null,
                                        photoPreview: currentUser.picture,
                                        isAdmin: true,
                                        status: 'active'  // Current user (owner) is always active
                                    }] : [];

                                    setFormData({
                                        photo: null,
                                        photoPreview: null,
                                        name: '',
                                        tagline: '',
                                        description: '',
                                        members: defaultMembers
                                    });
                                    setShowCreateForm(true);
                                }}
                                className="flex items-center gap-2 bg-primary-orange text-white px-6 py-3 rounded-lg font-semibold hover:scale-105 transform transition-all duration-200 shadow-lg"
                            >
                                <Plus className="w-5 h-5" />
                                <span>Create New Family</span>
                            </button>

                            {/* Golden Crown Overlay */}
                            {famlis.length >= 1 && (
                                <div className="absolute -top-2 -right-2 transform rotate-12">
                                    <Crown className="w-7 h-7 text-yellow-500 fill-yellow-400 drop-shadow-lg" />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Create/Edit Family Form Modal */}
                {showCreateForm && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
                        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                    >
                        {/* Backdrop with blur */}
                        <div
                            className="absolute inset-0 backdrop-blur-sm"
                            onClick={() => {
                                setShowCreateForm(false);
                                setEditingFamily(null);
                            }}
                        ></div>

                        {/* Modal Content - Widened by 20% (max-w-2xl to max-w-3xl) */}
                        <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-3xl w-full max-h-[90vh] flex flex-col transform transition-all duration-300 animate-slide-up modal-scroll">
                            <div className="flex justify-between items-center mb-6 flex-shrink-0">
                                <div className="flex-1">
                                    <h2 className="text-2xl font-bold text-primary-blue">
                                        {editingFamily ? (userPermissions?.can_edit ? 'Edit Family' : 'View Family') : 'Create Family'}
                                    </h2>
                                    {/* View-only banner */}
                                    {editingFamily && userPermissions && !userPermissions.can_edit && (
                                        <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
                                            <Shield className="w-5 h-5 text-yellow-600" />
                                            <p className="text-sm text-yellow-800">
                                                <span className="font-semibold">View-Only Mode:</span> You have read-only access to this family. Contact an admin to make changes.
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => {
                                        setShowCreateForm(false);
                                        setEditingFamily(null);
                                        setUserPermissions(null);
                                    }}
                                    className="text-gray-500 hover:text-gray-700 transition-colors ml-4"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6 overflow-y-auto flex-1">
                                {/* Photo Upload */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Family Photo (Optional)
                                    </label>
                                    <div className="flex items-center gap-4">
                                        {formData.photoPreview ? (
                                            <div className="relative">
                                                <img
                                                    src={formData.photoPreview}
                                                    alt="Preview"
                                                    className="w-24 h-24 rounded-full object-cover shadow-md"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setFormData({
                                                            ...formData,
                                                            photo: null,
                                                            photoPreview: null
                                                        });
                                                    }}
                                                    className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                                                    title="Delete photo"
                                                    disabled={editingFamily && userPermissions && !userPermissions.can_edit}
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-pink-500 flex items-center justify-center text-white text-3xl font-bold shadow-md">
                                                {formData.name ? getFamilyInitials(formData.name) : 'F'}
                                            </div>
                                        )}
                                        <ImageUploadWithPreview
                                            onImageSelect={handleFamilyPhotoSelect}
                                            currentImage={formData.photoPreview}
                                            aspectRatio={1}
                                        />
                                    </div>
                                </div>

                                {/* Family Name */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-sm font-semibold text-gray-700">
                                            Family Name *
                                        </label>
                                        <span className={`text-xs ${formData.name.length >= 64 ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                            {formData.name.length}/64
                                        </span>
                                    </div>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g., The Smiths"
                                        maxLength={64}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        required
                                        disabled={editingFamily && userPermissions && !userPermissions.can_edit}
                                    />
                                </div>

                                {/* Tagline */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-sm font-semibold text-gray-700">
                                            Tagline (Optional)
                                        </label>
                                        <span className={`text-xs ${formData.tagline.length >= 128 ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                            {formData.tagline.length}/128
                                        </span>
                                    </div>
                                    <input
                                        type="text"
                                        value={formData.tagline}
                                        onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                                        placeholder="e.g., Together is our favorite place to be"
                                        maxLength={128}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        disabled={editingFamily && userPermissions && !userPermissions.can_edit}
                                    />
                                </div>

                                {/* Description */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-sm font-semibold text-gray-700">
                                            Description (Optional)
                                        </label>
                                        <span className={`text-xs ${formData.description.length >= 2048 ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                            {formData.description.length}/2048
                                        </span>
                                    </div>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Tell us about your family..."
                                        rows="4"
                                        maxLength={2048}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        disabled={editingFamily && userPermissions && !userPermissions.can_edit}
                                    />
                                </div>

                                {/* Members List */}
                                <div ref={membersRef} className="transition-all duration-300 rounded-lg">
                                    <div className="flex items-center justify-between mb-3">
                                        <label className="block text-sm font-semibold text-gray-700">
                                            Family Members ({formData.members.length})
                                        </label>
                                        {/* Only show Add Member button if user is admin or creating new family */}
                                        {(!editingFamily || (userPermissions && userPermissions.is_admin)) && (
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        console.log('[Famlis] Add Member button clicked');
                                                        console.log('[Famlis] Current members count:', formData.members.length);
                                                        // Check if user has reached free tier limit (2 members)
                                                        if (formData.members.length >= 2) {
                                                            console.log('[Famlis] Free tier limit reached, showing premium modal');
                                                            setShowPremiumModal(true);
                                                            return;
                                                        }
                                                        console.log('[Famlis] Opening add member modal...');
                                                        openAddMemberModal();
                                                    }}
                                                    className="flex items-center gap-2 text-primary-blue hover:bg-primary-blue/10 px-3 py-1.5 rounded-lg font-semibold transition-all text-sm"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                    <span>Add Member</span>
                                                </button>

                                                {/* Golden Crown Overlay */}
                                                {formData.members.length >= 2 && (
                                                    <div className="absolute -top-1 -right-1 transform rotate-12">
                                                        <Crown className="w-5 h-5 text-yellow-500 fill-yellow-400 drop-shadow-lg" />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Members List Display */}
                                    <div className="space-y-2">
                                        {formData.members.length === 0 ? (
                                            <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                                                <User className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                                                <p>No members added yet</p>
                                                <p className="text-sm">Click "Add Member" to get started</p>
                                            </div>
                                        ) : (
                                            formData.members.map((member, index) => (
                                                <div key={index} className={`flex items-center justify-between p-3 rounded-lg transition-colors ${member.status === 'pending_invitation' ? 'bg-yellow-50 border border-yellow-200 opacity-75' : 'bg-gray-50 hover:bg-gray-100'}`}>
                                                    <div className="flex items-center gap-3">
                                                        {/* Member Photo with Shield for Admin */}
                                                        <div className="relative">
                                                            {member.status === 'pending_invitation' ? (
                                                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-400 flex items-center justify-center text-white font-bold border-2 border-yellow-300">
                                                                    {member.name.charAt(0).toUpperCase()}
                                                                </div>
                                                            ) : member.photoPreview ? (
                                                                <img
                                                                    src={member.photoPreview}
                                                                    alt={member.name}
                                                                    className="w-12 h-12 rounded-full object-cover border-2 border-gray-300"
                                                                />
                                                            ) : (
                                                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-blue to-primary-orange flex items-center justify-center text-white font-bold border-2 border-gray-300">
                                                                    {member.name.charAt(0).toUpperCase()}
                                                                </div>
                                                            )}
                                                            {member.isAdmin && member.status !== 'pending_invitation' && (
                                                                <div className="absolute -bottom-1 -right-1 bg-yellow-400 rounded-full p-1 border-2 border-white">
                                                                    <Shield className="w-3 h-3 text-yellow-800" />
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Member Info */}
                                                        <div>
                                                            <p className="font-semibold text-gray-800 flex items-center gap-2">
                                                                {member.status === 'pending_invitation' && <span className="text-yellow-600">⏳</span>}
                                                                {member.status !== 'pending_invitation' && <span className="text-green-600">✓</span>}
                                                                {member.name}
                                                                {member.isAdmin && (
                                                                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-medium">
                                                                        Admin
                                                                    </span>
                                                                )}
                                                            </p>
                                                            <p className="text-sm text-gray-600">{member.email}</p>
                                                            {member.status === 'pending_invitation' && (
                                                                <p className="text-xs text-yellow-700 font-medium mt-1">
                                                                    Pending Invitation - Not signed up yet
                                                                </p>
                                                            )}
                                                            {member.status === 'active' && (
                                                                <p className="text-xs text-green-700 font-medium mt-1">
                                                                    Active
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Actions Menu */}
                                                    <div className="relative" ref={activeMemberMenu === index ? memberMenuRef : null}>
                                                        <button
                                                            type="button"
                                                            onClick={() => setActiveMemberMenu(activeMemberMenu === index ? null : index)}
                                                            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                                                        >
                                                            <MoreVertical className="w-5 h-5 text-gray-600" />
                                                        </button>

                                                        {/* Dropdown Menu */}
                                                        {activeMemberMenu === index && (
                                                            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[100]">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openEditMemberModal(index)}
                                                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                                >
                                                                    <Edit2 className="w-4 h-4 text-primary-blue" />
                                                                    <span>Edit</span>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleAdmin(index)}
                                                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                                >
                                                                    <Crown className="w-4 h-4 text-yellow-600" />
                                                                    <span>{member.isAdmin ? 'Remove Admin' : 'Make Admin'}</span>
                                                                </button>
                                                                <div className="border-t border-gray-200 my-1"></div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (window.confirm(`Remove ${member.name} from the family?`)) {
                                                                            removeMember(index);
                                                                        }
                                                                    }}
                                                                    className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                    <span>Remove</span>
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCreateForm(false);
                                            setEditingFamily(null);
                                            setUserPermissions(null);
                                        }}
                                        className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                                    >
                                        {editingFamily && userPermissions && !userPermissions.can_edit ? 'Close' : 'Cancel'}
                                    </button>
                                    {/* Only show submit button if user can edit or is creating new family */}
                                    {(!editingFamily || (userPermissions && userPermissions.can_edit)) && (
                                        <button
                                            type="submit"
                                            className="flex-1 px-6 py-3 bg-primary-green text-white rounded-lg font-semibold hover:scale-105 transform transition-all duration-200 shadow-lg"
                                        >
                                            {editingFamily ? 'Update Family' : 'Create Family'}
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Add/Edit Member Modal */}
                {showMemberModal && (
                    <div
                        className="fixed inset-0 z-[1000] flex items-center justify-center p-4 animate-fade-in"
                        style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
                        onClick={(e) => {
                            // Only close if clicking the backdrop, not the modal content
                            if (e.target === e.currentTarget) {
                                setShowMemberModal(false);
                            }
                        }}
                    >
                        {/* Modal Content */}
                        <div
                            className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full transform transition-all duration-300 animate-slide-up"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xl font-bold text-primary-blue">
                                    {editingMemberIndex !== null ? 'Edit Member' : 'Add Member'}
                                </h3>
                                <button
                                    onClick={() => setShowMemberModal(false)}
                                    className="text-gray-500 hover:text-gray-700 transition-colors"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="space-y-4">
                                {/* Member Photo */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Photo (Optional)
                                    </label>
                                    <div className="flex items-center gap-4">
                                        {memberFormData.photoPreview ? (
                                            <img
                                                src={memberFormData.photoPreview}
                                                alt="Member preview"
                                                className="w-20 h-20 rounded-full object-cover border-2 border-gray-300"
                                            />
                                        ) : (
                                            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center border-2 border-gray-300">
                                                <User className="w-10 h-10 text-gray-400" />
                                            </div>
                                        )}
                                        <ImageUploadWithPreview
                                            onImageSelect={handleMemberPhotoSelect}
                                            currentImage={memberFormData.photoPreview}
                                            aspectRatio={1}
                                        />
                                    </div>
                                </div>

                                {/* Member Name */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={memberFormData.name}
                                        onChange={(e) => setMemberFormData({ ...memberFormData, name: e.target.value })}
                                        placeholder="Enter member name"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                                        required
                                    />
                                </div>

                                {/* Member Email */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Email *
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="email"
                                            value={memberFormData.email}
                                            onChange={(e) => setMemberFormData({ ...memberFormData, email: e.target.value })}
                                            onBlur={(e) => handleCheckUserEmail(e.target.value)}
                                            placeholder="Enter email address"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                                            required
                                            disabled={editingMemberIndex !== null}
                                        />
                                        {checkingUser && (
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                <Loader className="w-5 h-5 text-blue-600 animate-spin" />
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* User Status Indicator */}
                                    {userCheckResult && !checkingUser && (
                                        <div className={`mt-2 p-3 rounded-lg flex items-center gap-2 ${
                                            userCheckResult.exists 
                                                ? 'bg-green-50 border border-green-200' 
                                                : 'bg-yellow-50 border border-yellow-200'
                                        }`}>
                                            {userCheckResult.exists ? (
                                                <>
                                                    <span className="text-green-600 text-lg">✓</span>
                                                    <div className="flex-1">
                                                        <p className="text-sm font-semibold text-green-800">
                                                            User found! Will be added as Active member
                                                        </p>
                                                        <p className="text-xs text-green-700 mt-1">
                                                            {userCheckResult.name} is already registered and will get immediate access
                                                        </p>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="text-yellow-600 text-lg">⏳</span>
                                                    <div className="flex-1">
                                                        <p className="text-sm font-semibold text-yellow-800">
                                                            New user - Will receive invitation
                                                        </p>
                                                        <p className="text-xs text-yellow-700 mt-1">
                                                            An invitation will be sent when you create the family
                                                        </p>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Admin Checkbox */}
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        id="isAdmin"
                                        checked={memberFormData.isAdmin}
                                        onChange={(e) => setMemberFormData({ ...memberFormData, isAdmin: e.target.checked })}
                                        className="w-5 h-5 rounded border-gray-300 text-primary-blue focus:ring-primary-blue"
                                    />
                                    <label htmlFor="isAdmin" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                        <Crown className="w-4 h-4 text-yellow-600" />
                                        Make this member an admin
                                    </label>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowMemberModal(false)}
                                        className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSaveMember}
                                        className="flex-1 px-4 py-3 bg-primary-blue text-white rounded-lg font-semibold hover:scale-105 transform transition-all duration-200 shadow-lg"
                                    >
                                        {editingMemberIndex !== null ? 'Update' : 'Add'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Family List */}
                {famlis.length > 0 && (
                    <div className="max-w-4xl mx-auto">
                        <div className="space-y-4">
                            {famlis.map((famli) => (
                                <div
                                    key={famli.id}
                                    className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 p-6 flex items-center justify-between"
                                >
                                    {/* Left side - Family Info */}
                                    <div className="flex items-center gap-4 flex-1">
                                        <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
                                        {famli.photo ? (
                                            <img
                                                src={famli.photo}
                                                alt={famli.name}
                                                className="w-20 h-20 rounded-full object-cover shadow-md"
                                            />
                                        ) : (
                                            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-pink-500 flex items-center justify-center text-white text-2xl font-bold shadow-md">
                                                {getFamilyInitials(famli.name)}
                                            </div>
                                        )}
                                        <div>
                                            <h3 className="text-xl font-bold text-primary-blue mb-1">
                                                {famli.name}
                                            </h3>
                                            <p className="text-gray-600 italic">
                                                "{famli.tagline}"
                                            </p>
                                            <p className="text-sm text-gray-500 mt-1">
                                                {famli.members?.length || 0} member{(famli.members?.length || 0) !== 1 ? 's' : ''}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Right side - Menu */}
                                    <div
                                        className="relative"
                                        ref={(el) => familyMenuRefs.current[famli.id] = el}
                                    >
                                        <button
                                            onClick={() => toggleMenu(famli.id)}
                                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                        >
                                            <MoreVertical className="w-6 h-6 text-gray-600" />
                                        </button>

                                        {/* Dropdown Menu */}
                                        {activeMenu === famli.id && (
                                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                                                <button
                                                    onClick={() => handleEdit(famli)}
                                                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                >
                                                    <Edit className="w-5 h-5 text-primary-green" />
                                                    <span>Edit</span>
                                                </button>
                                                <button
                                                    onClick={() => handleEdit(famli, true)}
                                                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                >
                                                    <UserPlus className="w-5 h-5 text-primary-blue" />
                                                    <span>Add Members</span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setActiveMenu(null);
                                                        localStorage.setItem('selectedFamilyId', famli.id);
                                                        navigate('/settings');
                                                    }}
                                                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                >
                                                    <Settings className="w-5 h-5 text-gray-600" />
                                                    <span>Settings</span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        alert('Reorder feature - drag and drop the handle on the left!');
                                                        setActiveMenu(null);
                                                    }}
                                                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                >
                                                    <GripVertical className="w-5 h-5 text-gray-600" />
                                                    <span>Reorder</span>
                                                </button>
                                                <div className="border-t border-gray-200 my-2"></div>
                                                <button
                                                    onClick={() => {
                                                        if (window.confirm(`Are you sure you want to delete "${famli.name}"?`)) {
                                                            deleteFamli(famli.id);
                                                        }
                                                    }}
                                                    className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                    <span>Delete Family Group</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>

                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Famlis;

